import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { Users, Calendar, Music, Mail, Phone, Search, Download, Trash2, LogOut } from 'lucide-react';
import AdminLogin from '../components/AdminLogin';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [rsvps, setRsvps] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDay, setFilterDay] = useState('all'); // all, day1, day2, both

  useEffect(() => {
    // Check if already authenticated in this session
    const isAuth = sessionStorage.getItem('adminAuthenticated') === 'true';
    setAuthenticated(isAuth);
    
    if (isAuth) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = () => {
    setAuthenticated(true);
    fetchData();
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuthenticated');
    setAuthenticated(false);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [rsvpsResponse, statsResponse] = await Promise.all([
        axios.get(`${API}/rsvps`),
        axios.get(`${API}/rsvp/stats`)
      ]);
      setRsvps(rsvpsResponse.data);
      setStats(statsResponse.data);
    } catch (error) {
      console.error('Error fetching RSVP data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredRsvps = rsvps.filter(rsvp => {
    const matchesSearch = 
      rsvp.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rsvp.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (rsvp.guestNames && rsvp.guestNames.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesFilter = 
      filterDay === 'all' ||
      (filterDay === 'day1' && rsvp.attendingDay1) ||
      (filterDay === 'day2' && rsvp.attendingDay2) ||
      (filterDay === 'both' && rsvp.attendingDay1 && rsvp.attendingDay2);

    return matchesSearch && matchesFilter;
  });

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Guest Names', 'Day 1', 'Day 2', 'Number of Guests', 'Song Suggestion', 'Dietary Requirements', 'Submitted At'];
    const csvData = filteredRsvps.map(rsvp => [
      rsvp.fullName,
      rsvp.email,
      rsvp.phone || '',
      rsvp.guestNames || '',
      rsvp.attendingDay1 ? 'Yes' : 'No',
      rsvp.attendingDay2 ? 'Yes' : 'No',
      rsvp.numberOfGuests,
      rsvp.songSuggestion || '',
      rsvp.dietaryRequirements || '',
      new Date(rsvp.submittedAt).toLocaleString()
    ]);

    const csv = [headers, ...csvData].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'wedding-rsvps.csv';
    a.click();
  };

  const deleteRsvp = async (rsvpId) => {
    if (!window.confirm('Are you sure you want to delete this RSVP?')) {
      return;
    }

    try {
      await axios.delete(`${API}/rsvp/${rsvpId}`);
      // Refresh data after deletion
      fetchData();
    } catch (error) {
      console.error('Error deleting RSVP:', error);
      alert('Failed to delete RSVP. Please try again.');
    }
  };

  const deleteAllRsvps = async () => {
    if (!window.confirm('⚠️ WARNING: This will delete ALL RSVPs permanently. Are you absolutely sure?')) {
      return;
    }

    if (!window.confirm('This action cannot be undone. Click OK to confirm deletion of all RSVPs.')) {
      return;
    }

    try {
      await axios.delete(`${API}/rsvps/all`);
      // Refresh data after deletion
      fetchData();
      alert('All RSVPs have been deleted successfully.');
    } catch (error) {
      console.error('Error deleting all RSVPs:', error);
      alert('Failed to delete RSVPs. Please try again.');
    }
  };

  if (!authenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-ivory-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-lightskyblue-600 font-serif text-2xl">Loading RSVP data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div className="text-center flex-1">
            <h1 className="font-serif text-5xl text-lightskyblue-800 mb-2">RSVP Dashboard</h1>
            <p className="text-lightskyblue-600 font-body">Christie & Kieran's Wedding - August 22nd, 2026</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-lightskyblue-300 text-lightskyblue-700 hover:bg-lightskyblue-50"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>

        {/* Statistics Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card className="border-lightskyblue-200 shadow-lg">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-body text-lightskyblue-600 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Total RSVPs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-serif font-bold text-lightskyblue-800">{stats.totalRSVPs}</div>
                <p className="text-xs text-lightskyblue-600 mt-1">{stats.totalGuests} total guests</p>
              </CardContent>
            </Card>

            <Card className="border-lightskyblue-200 shadow-lg">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-body text-lightskyblue-600 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Day 1 - Saturday
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-serif font-bold text-lightskyblue-800">{stats.day1RSVPs}</div>
                <p className="text-xs text-lightskyblue-600 mt-1">{stats.day1Guests} guests</p>
              </CardContent>
            </Card>

            <Card className="border-lightskyblue-200 shadow-lg">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-body text-lightskyblue-600 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Day 2 - Sunday
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-serif font-bold text-lightskyblue-800">{stats.day2RSVPs}</div>
                <p className="text-xs text-lightskyblue-600 mt-1">{stats.day2Guests} guests</p>
              </CardContent>
            </Card>

            <Card className="border-lightskyblue-200 shadow-lg">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-body text-lightskyblue-600 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Both Days
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-serif font-bold text-lightskyblue-800">{stats.bothDaysRSVPs}</div>
                <p className="text-xs text-lightskyblue-600 mt-1">Attending both</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters and Search */}
        <Card className="mb-6 border-lightskyblue-200 shadow-lg">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-lightskyblue-400 w-5 h-5" />
                  <Input
                    type="text"
                    placeholder="Search by name, email, or guest names..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 border-lightskyblue-300 focus:border-lightskyblue-500"
                  />
                </div>
              </div>

              {/* Filter Buttons */}
              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={() => setFilterDay('all')}
                  variant={filterDay === 'all' ? 'default' : 'outline'}
                  className={filterDay === 'all' ? 'bg-lightskyblue-600 hover:bg-lightskyblue-700' : 'border-lightskyblue-300'}
                >
                  All
                </Button>
                <Button
                  onClick={() => setFilterDay('day1')}
                  variant={filterDay === 'day1' ? 'default' : 'outline'}
                  className={filterDay === 'day1' ? 'bg-lightskyblue-600 hover:bg-lightskyblue-700' : 'border-lightskyblue-300'}
                >
                  Day 1
                </Button>
                <Button
                  onClick={() => setFilterDay('day2')}
                  variant={filterDay === 'day2' ? 'default' : 'outline'}
                  className={filterDay === 'day2' ? 'bg-lightskyblue-600 hover:bg-lightskyblue-700' : 'border-lightskyblue-300'}
                >
                  Day 2
                </Button>
                <Button
                  onClick={() => setFilterDay('both')}
                  variant={filterDay === 'both' ? 'default' : 'outline'}
                  className={filterDay === 'both' ? 'bg-lightskyblue-600 hover:bg-lightskyblue-700' : 'border-lightskyblue-300'}
                >
                  Both Days
                </Button>
              </div>

              {/* Export Button */}
              <Button
                onClick={exportToCSV}
                className="bg-gold-500 hover:bg-gold-600 text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>

              {/* Delete All Button */}
              {rsvps.length > 0 && (
                <Button
                  onClick={deleteAllRsvps}
                  variant="destructive"
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete All
                </Button>
              )}
            </div>

            <div className="mt-4 text-sm text-lightskyblue-600 font-body">
              Showing {filteredRsvps.length} of {rsvps.length} RSVPs
            </div>
          </CardContent>
        </Card>

        {/* RSVP List */}
        <div className="space-y-4">
          {filteredRsvps.length === 0 ? (
            <Card className="border-lightskyblue-200">
              <CardContent className="p-8 text-center">
                <p className="text-lightskyblue-600 font-body">No RSVPs found matching your criteria.</p>
              </CardContent>
            </Card>
          ) : (
            filteredRsvps.map((rsvp) => (
              <Card key={rsvp.id} className="border-lightskyblue-200 shadow-md hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Left Column - Contact Info */}
                    <div>
                      <h3 className="font-serif text-xl text-lightskyblue-800 mb-2">{rsvp.fullName}</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-lightskyblue-600">
                          <Mail className="w-4 h-4" />
                          <a href={`mailto:${rsvp.email}`} className="hover:underline">{rsvp.email}</a>
                        </div>
                        {rsvp.phone && (
                          <div className="flex items-center gap-2 text-lightskyblue-600">
                            <Phone className="w-4 h-4" />
                            <a href={`tel:${rsvp.phone}`} className="hover:underline">{rsvp.phone}</a>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-lightskyblue-600">
                          <Users className="w-4 h-4" />
                          {rsvp.numberOfGuests} {rsvp.numberOfGuests === 1 ? 'guest' : 'guests'}
                        </div>
                      </div>
                    </div>

                    {/* Middle Column - Attendance & Guests */}
                    <div>
                      <div className="mb-3">
                        <p className="text-xs text-lightskyblue-600 font-semibold mb-2">ATTENDING:</p>
                        <div className="flex gap-2">
                          {rsvp.attendingDay1 && (
                            <Badge className="bg-lightskyblue-600 text-white">Day 1 - Saturday</Badge>
                          )}
                          {rsvp.attendingDay2 && (
                            <Badge className="bg-lightskyblue-500 text-white">Day 2 - Sunday</Badge>
                          )}
                        </div>
                      </div>
                      {rsvp.guestNames && (
                        <div>
                          <p className="text-xs text-lightskyblue-600 font-semibold mb-1">GUEST NAMES:</p>
                          <p className="text-sm text-lightskyblue-700 font-body whitespace-pre-line">{rsvp.guestNames}</p>
                        </div>
                      )}
                    </div>

                    {/* Right Column - Additional Info */}
                    <div>
                      {rsvp.songSuggestion && (
                        <div className="mb-3">
                          <p className="text-xs text-lightskyblue-600 font-semibold mb-1 flex items-center gap-1">
                            <Music className="w-3 h-3" />
                            SONG SUGGESTION:
                          </p>
                          <p className="text-sm text-lightskyblue-700 font-body italic">{rsvp.songSuggestion}</p>
                        </div>
                      )}
                      {rsvp.dietaryRequirements && (
                        <div className="mb-3">
                          <p className="text-xs text-lightskyblue-600 font-semibold mb-1">DIETARY:</p>
                          <p className="text-sm text-lightskyblue-700 font-body">{rsvp.dietaryRequirements}</p>
                        </div>
                      )}
                      <div className="text-xs text-lightskyblue-500 mt-2">
                        Submitted: {new Date(rsvp.submittedAt).toLocaleDateString()} at {new Date(rsvp.submittedAt).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>

                  {/* Delete Button */}
                  <div className="mt-4 pt-4 border-t border-lightskyblue-200 flex justify-end">
                    <Button
                      onClick={() => deleteRsvp(rsvp.id)}
                      variant="outline"
                      size="sm"
                      className="border-red-300 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Admin;